import React, { forwardRef, ReactNode, useRef, useState } from 'react';
import { flip, offset, shift } from '@floating-ui/dom';
import { arrow, FloatingArrow } from '@floating-ui/react';

import {
  AltTooltipDiv,
  TooltipDiv,
  TOOLTIP_COLOR,
  UnstyledTooltipDiv,
} from './styled';
import {
  useFloating,
  autoUpdate,
  useInteractions,
  useHover,
  useTransitionStyles,
} from '@floating-ui/react';
import ReactPortal from '../ReactPortal';
import { Z_INDEX } from '../../constants';

export const TOOLTIP_PORTAL_DIV_ID = 'TOOLTIP_PORTAL_DIV_ID';

const Anchor = forwardRef<
  HTMLDivElement,
  {
    children: JSX.Element | JSX.Element[] | React.ReactNode;
    isFlexAnchor?: boolean;
    testId?: string;
  }
>(
  (
    {
      children,
      isFlexAnchor,
      testId,
    }: {
      children: JSX.Element | JSX.Element[] | React.ReactNode;
      isFlexAnchor?: boolean;
      testId?: string;
    },
    ref,
  ) => (
    <div
      ref={ref}
      style={{ overflow: 'hidden', display: isFlexAnchor ? 'flex' : 'block' }}
      className={'tooltip-anchor'}
      data-testid={testId}
    >
      {children}
    </div>
  ),
);

const Tooltip = ({
  content,
  position,
  children,
  isUnstyledTooltip,
  isAltTooltip,
  isFlexAnchor,
  shouldMaintainConstantDomTree,
  testId,
  delay,
  hideDelay,
}: {
  content:
    | ReactNode
    | JSX.Element
    | JSX.Element[]
    | (JSX.Element | undefined | string | number)[]
    | string
    | string[]
    | number
    | undefined
    | null;
  position?: 'top' | 'left' | 'bottom' | 'right' | 'bottom-start';
  children: JSX.Element | JSX.Element[] | React.ReactNode;
  isUnstyledTooltip?: boolean;
  isAltTooltip?: boolean;
  hideTooltipOnClick?: boolean;
  onLongOpenClosed?: () => void;
  tag?: string;
  delay?: number;
  isFlexAnchor?: boolean;
  shouldMaintainConstantDomTree?: boolean;
  testId?: string;
  hideDelay?: number;
}) => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const TooltipComponent = isUnstyledTooltip
    ? UnstyledTooltipDiv
    : isAltTooltip
      ? AltTooltipDiv
      : TooltipDiv;
  const { refs, context, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position,
    // Fixed, not absolute: an absolutely positioned tooltip shifted against the
    // viewport edge (e.g. in the board slideout) can overflow its container by a
    // sub-pixel, toggling a scrollbar that narrows the clipping rect — shift()
    // then moves it, the scrollbar goes, and it oscillates every frame (a
    // visible blur). Fixed elements don't contribute to ancestor overflow.
    strategy: 'fixed',
    middleware: [
      flip(),
      offset(8),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });
  const hover = useHover(
    context,
    process.env.NODE_ENV === 'test'
      ? undefined
      : {
          delay: { open: delay ?? 1000, close: hideDelay ?? 0 },
        },
  );
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: {
      open: 140,
      close: 90,
    },

    initial: {
      opacity: 0,
      transform: 'scale(0.9)',
    },

    open: {
      opacity: 1,
      transform: 'scale(1)',
    },
    close: {
      opacity: 0,
      transform: 'scale(0.9)',
    },
  });

  if (!content && !shouldMaintainConstantDomTree) {
    return <>{children}</>;
  }

  const arrowColor = isAltTooltip ? 'white' : TOOLTIP_COLOR;

  return (
    <>
      <Anchor
        ref={refs.setReference}
        {...getReferenceProps()}
        isFlexAnchor={isFlexAnchor}
        testId={testId}
      >
        {children}
      </Anchor>
      {isMounted && (
        <ReactPortal elementId={TOOLTIP_PORTAL_DIV_ID} isTestModeDisabled>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: Z_INDEX.TOOLTIP,
            }}
            {...getFloatingProps()}
          >
            <div style={styles}>
              <FloatingArrow
                ref={arrowRef}
                context={context}
                fill={arrowColor}
                stroke={arrowColor}
                color={arrowColor}
              />
              <TooltipComponent>{isMounted && <>{content}</>}</TooltipComponent>
            </div>
          </div>
        </ReactPortal>
      )}
    </>
  );
};

export default Tooltip;
